import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect } from 'react';
const CompactVolumeSlider = ({ volume, onVolumeChange, isFeatured = false, className = '' }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [draggedButton, setDraggedButton] = useState(null);
    const [sliderWidth, setSliderWidth] = useState(isFeatured ? 854 : 310);
    const dragStartPosRef = useRef({ x: 0, volume: 0 });
    const sliderRef = useRef(null);
    // Sizes based on mode
    const buttonSize = isFeatured ? 16 : 12; // Even smaller buttons
    const sliderHeight = isFeatured ? 22 : 22; // Regular tile 4px taller (was 18, now 22)
    // Update slider width when in featured mode based on actual container width
    useEffect(() => {
        if (isFeatured && sliderRef.current) {
            const updateWidth = () => {
                if (sliderRef.current) {
                    setSliderWidth(sliderRef.current.offsetWidth || 854);
                }
            };
            updateWidth();
            window.addEventListener('resize', updateWidth);
            return () => window.removeEventListener('resize', updateWidth);
        }
        else {
            setSliderWidth(310);
        }
    }, [isFeatured]);
    const getButtonPositions = () => {
        const offset = (volume / 100) * ((sliderWidth - buttonSize) / 2);
        return {
            left: (sliderWidth / 2) - (buttonSize / 2) - offset,
            right: (sliderWidth / 2) - (buttonSize / 2) + offset
        };
    };
    const positions = getButtonPositions();
    const handleMouseDown = (e, button) => {
        e.stopPropagation();
        setIsDragging(true);
        setDraggedButton(button);
        dragStartPosRef.current = { x: e.clientX, volume: volume };
    };
    const handleMove = (clientX) => {
        if (!isDragging)
            return;
        const deltaX = clientX - dragStartPosRef.current.x;
        const maxDistance = (sliderWidth - buttonSize) / 2;
        let volumeChange = (deltaX / maxDistance) * 100;
        if (draggedButton === 'left')
            volumeChange = -volumeChange;
        const newVolume = Math.max(0, Math.min(100, dragStartPosRef.current.volume + volumeChange));
        onVolumeChange(newVolume);
    };
    const handleMouseMove = (e) => handleMove(e.clientX);
    const handleEnd = () => { setIsDragging(false); setDraggedButton(null); };
    const handleButtonClick = (e) => {
        e.stopPropagation();
        onVolumeChange(volume === 0 ? 50 : 0);
    };
    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleEnd);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleEnd);
            };
        }
    }, [isDragging, volume]);
    const isMerged = volume === 0;
    const lineOpacity = Math.pow(volume / 100, 0.7);
    return (_jsxs("div", { ref: sliderRef, className: `compact-volume-slider ${className}`, style: {
            position: 'absolute',
            bottom: '0',
            left: '0',
            width: isFeatured ? '100%' : `${sliderWidth}px`,
            height: `${sliderHeight}px`,
            background: 'rgba(0, 0, 0, 0.85)',
            border: 'none',
            borderTop: '1px solid rgba(139, 92, 246, 0.4)',
            boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 100,
            pointerEvents: 'auto'
        }, onClick: (e) => e.stopPropagation(), children: [!isMerged && (_jsx("div", { style: {
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${volume}%`,
                    background: 'linear-gradient(90deg, rgba(139, 92, 246, 0.3), rgba(168, 85, 247, 0.4))',
                    pointerEvents: 'none'
                } })), isMerged && (_jsx("div", { style: {
                    position: 'absolute',
                    left: '50%',
                    top: isFeatured ? '45%' : '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: isFeatured ? '20px' : '18px',
                    color: 'rgba(255, 255, 255, 0.95)',
                    pointerEvents: 'none',
                    zIndex: 101,
                    textShadow: '0 2px 6px rgba(0, 0, 0, 0.8)',
                    filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.2))'
                }, children: "\u00F0\u0178\u201D\u2021" })), isMerged && (_jsx("div", { onMouseDown: (e) => {
                    e.stopPropagation();
                    setIsDragging(true);
                    const centerX = e.currentTarget.getBoundingClientRect().left + (e.currentTarget.getBoundingClientRect().width / 2);
                    const clickX = e.clientX;
                    setDraggedButton(clickX < centerX ? 'left' : 'right');
                    dragStartPosRef.current = { x: e.clientX, volume: volume };
                }, onClick: handleButtonClick, style: {
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: `${buttonSize * 3}px`,
                    height: `${buttonSize * 3}px`,
                    cursor: 'grab',
                    zIndex: 102,
                    opacity: 0
                } })), !isMerged && (_jsx("div", { style: {
                    position: 'absolute',
                    top: '50%',
                    left: `${positions.left + buttonSize}px`,
                    width: `${positions.right - positions.left - buttonSize}px`,
                    height: '2px',
                    background: `rgba(139, 92, 246, ${lineOpacity})`,
                    transform: 'translateY(-50%)',
                    borderRadius: '1px',
                    opacity: lineOpacity,
                    boxShadow: `0 0 6px rgba(139, 92, 246, ${lineOpacity * 0.5})`,
                    pointerEvents: 'none'
                } })), !isMerged && (_jsx("div", { onMouseDown: (e) => handleMouseDown(e, 'left'), style: {
                    position: 'absolute',
                    left: `${positions.left}px`,
                    top: '50%',
                    transform: `translateY(-50%) scale(${isDragging && draggedButton === 'left' ? 1.15 : 1})`,
                    width: `${buttonSize}px`,
                    height: `${buttonSize}px`,
                    borderRadius: '50%',
                    background: isDragging && draggedButton === 'left'
                        ? 'linear-gradient(135deg, #a78bfa, #8b5cf6)'
                        : 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                    cursor: isDragging && draggedButton === 'left' ? 'grabbing' : 'grab',
                    boxShadow: isDragging && draggedButton === 'left'
                        ? '0 3px 10px rgba(139, 92, 246, 0.9)'
                        : '0 2px 8px rgba(139, 92, 246, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isFeatured ? '14px' : '11px',
                    zIndex: isDragging && draggedButton === 'left' ? 15 : 5,
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    transition: isDragging ? 'transform 0.03s ease' : 'background 0.15s ease, transform 0.03s ease',
                    userSelect: 'none'
                } })), !isMerged && (_jsx("div", { onMouseDown: (e) => handleMouseDown(e, 'right'), style: {
                    position: 'absolute',
                    left: `${positions.right}px`,
                    top: '50%',
                    transform: `translateY(-50%) scale(${isDragging && draggedButton === 'right' ? 1.15 : 1})`,
                    width: `${buttonSize}px`,
                    height: `${buttonSize}px`,
                    borderRadius: '50%',
                    background: isDragging && draggedButton === 'right'
                        ? 'linear-gradient(135deg, #a78bfa, #8b5cf6)'
                        : 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                    cursor: isDragging && draggedButton === 'right' ? 'grabbing' : 'grab',
                    boxShadow: isDragging && draggedButton === 'right'
                        ? '0 3px 10px rgba(139, 92, 246, 0.9)'
                        : '0 2px 8px rgba(139, 92, 246, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isFeatured ? '14px' : '11px',
                    zIndex: isDragging && draggedButton === 'right' ? 15 : 5,
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    opacity: lineOpacity,
                    transition: isDragging ? 'transform 0.03s ease' : 'opacity 0.1s ease, transform 0.03s ease',
                    userSelect: 'none'
                } })), isFeatured && (_jsxs("div", { style: {
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '13px',
                    fontWeight: '600',
                    pointerEvents: 'none',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.8)'
                }, children: [Math.round(volume), "%"] }))] }));
};
export default CompactVolumeSlider;
